import { reactive } from 'vue'
import {
  cancelWorkflowRun,
  createWorkflowRun,
  getPublicSession,
  getWorkflowRun,
  listWorkflowRunsForWorkflow,
  subscribeWorkflowRun
} from '@/utils/api'
import { isApiError } from '@/types/api'
import type { WorkflowRunRecord, WorkflowRunUsage } from '@/types/workflows'
import { formatOwnedAgentRef } from '@/utils/agentRefs'

export interface HelperAgentState {
  helperRunId: string
  agentRef: string
  agentName: string
  agentId: string
  agentUsername: string
  agentSlug: string
  chatId: string | null
  sessionId: string | null
  index: number
  total: number
  status: 'pending' | 'queued' | 'running' | 'completed' | 'error' | 'cancelled'
  content: string
  error: string | null
  usage: WorkflowRunUsage | null
  toolEvents: Array<{ type: string; data?: any; message?: string; sessionId?: string }>
}

export interface WorkflowStreamState {
  status: 'idle' | 'queued' | 'running' | 'completed' | 'error' | 'cancelled'
  helpers: HelperAgentState[]
  mainContent: string
  mainChatId: string | null
  mainToolEvents: Array<{ type: string; data?: any; message?: string; sessionId?: string }>
  mainStatus: 'pending' | 'queued' | 'running' | 'completed' | 'error' | 'cancelled'
  mainUsage: WorkflowRunUsage | null
  finalResult: any | null
  error: string | null
  usage: WorkflowRunUsage | null
  runId: string | null
}

const TERMINAL_CARD_STATUSES: readonly string[] = ['completed', 'error', 'cancelled']

function isTerminalCardStatus(status: string): boolean {
  return TERMINAL_CARD_STATUSES.includes(status)
}

export function useWorkflowStream() {
  const state = reactive<WorkflowStreamState>({
    status: 'idle',
    helpers: [],
    mainContent: '',
    mainChatId: null,
    mainToolEvents: [],
    mainStatus: 'pending',
    mainUsage: null,
    finalResult: null,
    error: null,
    usage: null,
    runId: null
  })

  let runStreamUnsubscribe: (() => void) | null = null
  let activeWorkflowId: string | null = null

  function getStorageKey(workflowId: string): string {
    return `msq.workflow.run.${workflowId}`
  }

  function clearStoredRunId(runId: string): void {
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i)
      if (key?.startsWith('msq.workflow.run.')) {
        try {
          if (sessionStorage.getItem(key) === runId) {
            sessionStorage.removeItem(key)
          }
        } catch {
          /* ignore */
        }
      }
    }
  }

  function reset(): void {
    state.status = 'idle'
    state.helpers = []
    state.mainContent = ''
    state.mainChatId = null
    state.mainToolEvents = []
    state.mainStatus = 'pending'
    state.mainUsage = null
    state.finalResult = null
    state.error = null
    state.usage = null
    state.runId = null
    if (runStreamUnsubscribe) {
      runStreamUnsubscribe()
      runStreamUnsubscribe = null
    }
  }

  function upsertHelper(helper: HelperAgentState): HelperAgentState {
    const existingIndex = state.helpers.findIndex((entry) => entry.helperRunId === helper.helperRunId)
    if (existingIndex === -1) {
      state.helpers.push(helper)
      return helper
    }
    state.helpers[existingIndex] = { ...state.helpers[existingIndex], ...helper }
    return state.helpers[existingIndex]
  }

  async function hydrateChatArtifacts(record: WorkflowRunRecord): Promise<void> {
    const hydrateHelper = async (helper: HelperAgentState) => {
      if (!helper.chatId) return
      const response = await getPublicSession(helper.agentUsername || record.ownerUserId, helper.agentSlug, helper.chatId)
      if (isApiError(response) || !response.success || !response.data?.messages?.length) return
      const assistantMessage = [...response.data.messages].reverse().find((message) => message.role === 'assistant')
      if (!assistantMessage) return
      helper.content =
        typeof assistantMessage.content === 'string'
          ? assistantMessage.content
          : helper.content
      helper.usage = assistantMessage.tokenUsage || helper.usage
      helper.toolEvents = assistantMessage.toolEvents || helper.toolEvents
    }

    const hydrateMain = async () => {
      if (!record.main.chatId || !record.main.agentSlug) return
      const response = await getPublicSession(
        record.main.agentUsername ?? record.ownerUserId,
        record.main.agentSlug,
        record.main.chatId
      )
      if (isApiError(response) || !response.success || !response.data?.messages?.length) return
      const assistantMessage = [...response.data.messages].reverse().find((message) => message.role === 'assistant')
      if (!assistantMessage) return
      if (typeof assistantMessage.content === 'string') {
        state.mainContent = assistantMessage.content
      }
      state.mainUsage = assistantMessage.tokenUsage || state.mainUsage
      state.mainToolEvents = assistantMessage.toolEvents || state.mainToolEvents
    }

    await Promise.allSettled([
      ...state.helpers.map((helper) => hydrateHelper(helper)),
      hydrateMain()
    ])
  }

  function applySnapshot(record: WorkflowRunRecord): void {
    state.runId = record.runId
    state.status =
      record.status === 'queued'
        ? 'queued'
        : record.status === 'running'
          ? 'running'
          : record.status === 'cancelled'
            ? 'cancelled'
          : record.status === 'completed'
            ? 'completed'
            : 'error'
    state.error =
      record.status === 'error'
        ? record.errorMessage || 'Workflow failed'
        : record.status === 'cancelled'
          ? 'Workflow was cancelled'
          : null
    state.usage = record.aggregateUsage
    state.helpers = record.resumeSnapshot.helpers.map((helper) => ({
      helperRunId: helper.helperRunId,
      agentRef: helper.agentRef || formatOwnedAgentRef(helper.agentId),
      agentName: helper.agentName,
      agentId: helper.agentId,
      agentUsername: helper.agentUsername || record.ownerUserId,
      agentSlug: helper.agentSlug,
      chatId: helper.chatId,
      sessionId: helper.sessionId,
      index: helper.patternIndex,
      total: record.resumeSnapshot.helpers.length,
      status: helper.status,
      content: helper.previewContent || '',
      error: helper.errorMessage || null,
      usage: helper.usage,
      toolEvents: []
    }))
    state.mainContent = record.resumeSnapshot.main.previewContent || ''
    state.mainChatId = record.resumeSnapshot.main.chatId || null
    state.mainToolEvents = []
    state.mainStatus = record.resumeSnapshot.main.status
    state.mainUsage = record.resumeSnapshot.main.usage
    if (record.status === 'completed') {
      state.finalResult = { content: state.mainContent }
    }
    void hydrateChatArtifacts(record)
  }

  function handleEvent(event: any): void {
    switch (event.type) {
      case 'run_started':
        state.status = 'running'
        break

      case 'helper_started': {
        upsertHelper({
          helperRunId: event.helperRunId,
          agentRef: event.agentRef || formatOwnedAgentRef(event.agentId),
          agentName: event.agentName,
          agentId: event.agentId,
          agentUsername: event.agentUsername || '',
          agentSlug: event.agentSlug,
          chatId: event.chatId || null,
          sessionId: event.sessionId || null,
          index: event.patternIndex,
          total: event.total ?? state.helpers.length + 1,
          status: 'running',
          content: '',
          error: null,
          usage: null,
          toolEvents: []
        })
        state.status = 'running'
        break
      }

      case 'helper_content_delta': {
        const helper = state.helpers.find((entry) => entry.helperRunId === event.helperRunId)
        if (helper) helper.content += event.delta
        break
      }

      case 'helper_tool_event': {
        const helper = state.helpers.find((entry) => entry.helperRunId === event.helperRunId)
        if (helper) helper.toolEvents.push(event.toolEvent)
        break
      }

      case 'helper_usage': {
        const helper = state.helpers.find((entry) => entry.helperRunId === event.helperRunId)
        if (helper) helper.usage = event.usage || null
        break
      }

      case 'helper_completed': {
        const helper = state.helpers.find((entry) => entry.helperRunId === event.helperRunId)
        if (helper) {
          helper.status = 'completed'
          helper.content = event.result || helper.content
          helper.usage = event.usage || helper.usage
        }
        break
      }

      case 'helper_error': {
        const helper = state.helpers.find((entry) => entry.helperRunId === event.helperRunId)
        if (helper) {
          helper.status = 'error'
          helper.error = event.message || 'Helper failed'
        }
        break
      }

      case 'main_started':
        state.mainStatus = 'running'
        state.mainChatId = event.chatId || null
        state.status = 'running'
        break

      case 'main_tool_event':
        state.mainToolEvents.push(event.toolEvent)
        if (state.mainStatus === 'pending') {
          state.mainStatus = 'running'
        }
        state.status = 'running'
        break

      case 'main_content_delta':
        state.mainContent += event.delta
        state.mainStatus = 'running'
        break

      case 'main_usage':
        state.mainUsage = event.usage || null
        break

      case 'main_completed':
        state.mainStatus = 'completed'
        state.mainChatId = event.chatId || state.mainChatId
        if (event.result?.content) {
          state.mainContent = event.result.content
          state.finalResult = event.result
        }
        state.mainUsage = event.usage || state.mainUsage
        break

      case 'workflow_completed':
        state.status = 'completed'
        state.usage = event.aggregateUsage || event.usage || null
        break

      case 'workflow_cancelled':
        state.status = 'cancelled'
        state.error = 'Workflow was cancelled'
        // The api cancels every non-terminal helper and the main agent together with the run;
        // mirror that here so sub-agent cards don't stay frozen on their last live status.
        // Inverted check so any future intermediate status cancels by default too.
        for (const helper of state.helpers) {
          if (!isTerminalCardStatus(helper.status)) {
            helper.status = 'cancelled'
          }
        }
        if (!isTerminalCardStatus(state.mainStatus)) {
          state.mainStatus = 'cancelled'
        }
        break

      case 'error':
        state.status = 'error'
        state.error = event.message || 'Workflow execution failed'
        break

      default:
        break
    }
  }

  async function reconcileStreamDone(): Promise<void> {
    if (state.status !== 'running' && state.status !== 'queued') return

    const runId = state.runId
    if (!runId) {
      state.status = 'error'
      state.error = 'Workflow run stream ended before a run id was assigned.'
      return
    }

    const response = await getWorkflowRun(runId)
    if (!isApiError(response) && response.success) {
      applySnapshot(response.data)
      if (response.data.status === 'completed' || response.data.status === 'error' || response.data.status === 'cancelled') {
        return
      }
    }

    if (state.status === 'running' || state.status === 'queued') {
      state.status = 'error'
      state.error = 'Workflow run stream ended before completion. Refresh the run to check the latest status.'
    }
  }

  async function subscribeToRun(runId: string): Promise<void> {
    if (runStreamUnsubscribe) {
      runStreamUnsubscribe()
      runStreamUnsubscribe = null
    }

    runStreamUnsubscribe = subscribeWorkflowRun(runId, {
      onSnapshot: (record) => {
        applySnapshot(record)
      },
      onEvent: (event) => {
        handleEvent(event)
      },
      onDone: () => {
        void reconcileStreamDone()
        runStreamUnsubscribe = null
      },
      onError: (message) => {
        state.error = message
        state.status = 'error'
        runStreamUnsubscribe = null
      }
    })
  }

  async function execute(workflowId: string): Promise<void> {
    reset()
    activeWorkflowId = workflowId
    state.status = 'queued'

    const response = await createWorkflowRun(workflowId)
    if (isApiError(response) || !response.success) {
      state.status = 'error'
      state.error = isApiError(response) ? response.message : 'Failed to start workflow'
      return
    }

    state.runId = response.runId
    try { sessionStorage.setItem(getStorageKey(workflowId), response.runId) } catch { /* ignore */ }
    if (response.data) {
      applySnapshot(response.data)
    }
    await subscribeToRun(response.runId)
  }

  async function cancel(): Promise<void> {
    if (!state.runId) return
    const response = await cancelWorkflowRun(state.runId)
    if (!isApiError(response)) {
      clearStoredRunId(state.runId)
      state.error = 'Workflow was cancelled'
      state.status = 'cancelled'
    }
    if (runStreamUnsubscribe) {
      runStreamUnsubscribe()
      runStreamUnsubscribe = null
    }
  }

  async function resume(workflowId: string, options: { onlyActive?: boolean; runId?: string } = {}): Promise<boolean> {
    let storedRunId: string | null = null
    try { storedRunId = sessionStorage.getItem(getStorageKey(workflowId)) } catch { /* ignore */ }

    activeWorkflowId = workflowId

    let record: WorkflowRunRecord | null = null

    if (options.runId) {
      const response = await getWorkflowRun(options.runId)
      if (!isApiError(response) && response.success && response.data.workflowConfigId === workflowId) {
        record = response.data
        try { sessionStorage.setItem(getStorageKey(workflowId), record.runId) } catch { /* ignore */ }
      }
    }

    if (!record && storedRunId) {
      const response = await getWorkflowRun(storedRunId)
      if (!isApiError(response) && response.success) {
        record = response.data
      } else {
        clearStoredRunId(storedRunId)
      }
    }

    if (!record) {
      const listResponse = await listWorkflowRunsForWorkflow(workflowId, 1, 0)
      if (isApiError(listResponse) || !listResponse.success || !listResponse.data.length) {
        return false
      }
      record = listResponse.data[0]
      try { sessionStorage.setItem(getStorageKey(workflowId), record.runId) } catch { /* ignore */ }
    }

    const isTerminal = record.status === 'completed' || record.status === 'error' || record.status === 'cancelled'
    if (options.onlyActive && isTerminal) {
      clearStoredRunId(record.runId)
      return false
    }

    applySnapshot(record)

    if (isTerminal) {
      return true
    }

    await subscribeToRun(record.runId)
    return true
  }

  return {
    state,
    execute,
    cancel,
    reset,
    resume,
    get activeWorkflowId() {
      return activeWorkflowId
    }
  }
}
